import {
  useEffect,
  useId,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
} from 'react';
import { InputAdornment, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router';

import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import { authClient, PROVIDER_NAME, type OAuthProvider } from './authClient';
import { ERROR_TEXT, fieldSx, type PasswordCheck } from './authForm';

/**
 * The pieces every account page is built from.
 *
 * One set, so the four pages cannot drift into four slightly different forms —
 * which is how a sign-up page ends up with a password field that behaves
 * differently from the log-in page's.
 */

const Title = styled('h1')({
  margin: 0,
  fontFamily: FONTS.serif,
  fontWeight: 700,
  fontSize: 32,
  lineHeight: 1.12,
  letterSpacing: '-0.01em',
  // Focus lands here on every route change; the ring would read as a bug.
  '&:focus': { outline: 'none' },
});

const Lede = styled('p')({
  margin: '10px 0 0',
  fontFamily: FONTS.body,
  fontSize: 15,
  lineHeight: 1.55,
  color: 'rgba(243,238,231,0.78)',
});

export function PageHeading({ title, lede }: { title: string; lede?: ReactNode }) {
  const heading = useRef<HTMLHeadingElement | null>(null);

  /**
   * Moving between these pages is a route change rather than a page load, so a
   * screen reader announces nothing unless focus moves to where the new page
   * begins. The heading takes it, rather than the first field: an autofocused
   * field on a phone throws the keyboard up over the page before it has been
   * read.
   */
  useEffect(() => {
    heading.current?.focus();
  }, [title]);

  return (
    <header style={{ marginBottom: 26 }}>
      <Title ref={heading} tabIndex={-1}>
        {title}
      </Title>
      {lede && <Lede>{lede}</Lede>}
    </header>
  );
}

const FormStackRoot = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
});

export function EmailField({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (value: string) => void;
  error?: string;
}) {
  return (
    <TextField
      label="Email"
      type="email"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      error={Boolean(error)}
      // A space rather than nothing: the line is reserved, so an error arriving
      // does not shove the rest of the form down under the thumb.
      helperText={error || ' '}
      fullWidth
      slotProps={{
        htmlInput: {
          autoComplete: 'email',
          inputMode: 'email',
          autoCapitalize: 'none',
          autoCorrect: 'off',
          spellCheck: false,
        },
      }}
      sx={fieldSx}
    />
  );
}

const Reveal = styled('button')({
  appearance: 'none',
  background: 'none',
  border: 'none',
  padding: '6px 4px',
  cursor: 'pointer',
  fontFamily: FONTS.body,
  fontSize: 13,
  fontWeight: 700,
  color: BRAND.parchment,
  '&:hover': { opacity: 0.75 },
});

/**
 * A password field with a Show button instead of a "confirm password" field.
 *
 * Typing a password twice exists to catch a typo you cannot see. Seeing it
 * catches the same typo in half the typing, and on a phone keyboard that is
 * most of the effort of signing up.
 */
export function PasswordField({
  label = 'Password',
  value,
  onChange,
  autoComplete,
  error,
  describedBy,
}: {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  /** `new-password` when choosing one, `current-password` when logging in. */
  autoComplete: 'new-password' | 'current-password';
  error?: string;
  /** The id of anything else that explains the field, such as its checklist. */
  describedBy?: string;
}) {
  const [shown, setShown] = useState(false);
  const id = useId();

  return (
    <TextField
      id={id}
      label={label}
      type={shown ? 'text' : 'password'}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      error={Boolean(error)}
      helperText={error || ' '}
      fullWidth
      slotProps={{
        htmlInput: {
          autoComplete,
          autoCapitalize: 'none',
          autoCorrect: 'off',
          spellCheck: false,
          'aria-describedby': [`${id}-helper-text`, describedBy].filter(Boolean).join(' '),
        },
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <Reveal
                type="button"
                onClick={() => setShown((v) => !v)}
                aria-pressed={shown}
                aria-label={shown ? 'Hide password' : 'Show password'}
              >
                {shown ? 'Hide' : 'Show'}
              </Reveal>
            </InputAdornment>
          ),
        },
      }}
      sx={fieldSx}
    />
  );
}

const Checklist = styled('ul')({
  listStyle: 'none',
  margin: '-6px 0 8px',
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontFamily: FONTS.body,
  fontSize: 13,
  color: 'rgba(243,238,231,0.62)',
  '& li[data-met="true"]': { color: BRAND.parchment },
});

const Tick = styled('span')({ display: 'inline-block', width: 18, fontWeight: 700 });

const Hidden = styled('span')({
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
});

/** The requirements, ticked off while typing rather than announced as a failure. */
export function PasswordChecklist({ id, checks }: { id: string; checks: PasswordCheck[] }) {
  return (
    <Checklist id={id} aria-live="polite">
      {checks.map((check) => (
        <li key={check.id} data-met={check.met}>
          <Tick aria-hidden="true">{check.met ? '✓' : '·'}</Tick>
          {check.label}
          <Hidden>{check.met ? ' — done' : ' — not yet'}</Hidden>
        </li>
      ))}
    </Checklist>
  );
}

const primaryStyles = {
  appearance: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  minHeight: 50,
  marginTop: 6,
  padding: '0 20px',
  borderRadius: 999,
  border: 'none',
  cursor: 'pointer',
  fontFamily: FONTS.body,
  fontSize: 16,
  fontWeight: 700,
  textDecoration: 'none',
  background: BRAND.parchment,
  color: BRAND.mocha,
  transition: 'background 120ms ease',
  '&:hover': { background: '#fff' },
  '&:disabled': { opacity: 0.6, cursor: 'default' },
} as const;

const Primary = styled('button')(primaryStyles);

export function SubmitButton({
  busy,
  busyLabel,
  children,
}: {
  busy: boolean;
  busyLabel: string;
  children: ReactNode;
}) {
  return (
    <Primary type="submit" disabled={busy} aria-busy={busy}>
      {busy ? busyLabel : children}
    </Primary>
  );
}

const PrimaryLinkRoot = styled(Link)(primaryStyles);

const ErrorBox = styled('p')({
  margin: '2px 0 4px',
  padding: '10px 12px',
  borderRadius: 10,
  fontFamily: FONTS.body,
  fontSize: 14,
  lineHeight: 1.45,
  color: BRAND.parchment,
  background: 'rgba(239,61,35,0.16)',
  border: `1px solid ${ERROR_TEXT}`,
});

export function FormError({ message }: { message: string }) {
  return message ? <ErrorBox role="alert">{message}</ErrorBox> : null;
}

const Rule = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  margin: '22px 0 18px',
  fontFamily: FONTS.body,
  fontSize: 13,
  color: 'rgba(243,238,231,0.55)',
  '&::before, &::after': {
    content: '""',
    flex: 1,
    borderTop: '1px solid rgba(243,238,231,0.18)',
  },
});

export function OrDivider({ label = 'or' }: { label?: string }) {
  return <Rule role="separator">{label}</Rule>;
}

const NoteRoot = styled('p')({
  margin: '14px 0 0',
  fontFamily: FONTS.body,
  fontSize: 13.5,
  lineHeight: 1.55,
  color: 'rgba(243,238,231,0.68)',
  '& a': { color: BRAND.parchment, fontWeight: 700 },
});

const RouterLinkRoot = styled(Link)({
  color: BRAND.parchment,
  fontWeight: 700,
  textUnderlineOffset: 3,
});

const FooterRoot = styled('p')({
  margin: '28px 0 0',
  paddingTop: 20,
  borderTop: '1px solid rgba(243,238,231,0.14)',
  fontFamily: FONTS.body,
  fontSize: 14.5,
  textAlign: 'center',
  color: 'rgba(243,238,231,0.78)',
});

const SocialStack = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
});

const SocialButton = styled('button')({
  appearance: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  width: '100%',
  minHeight: 50,
  padding: '0 18px',
  borderRadius: 999,
  cursor: 'pointer',
  fontFamily: FONTS.body,
  fontSize: 15.5,
  fontWeight: 700,
  color: BRAND.parchment,
  background: 'transparent',
  border: '1px solid rgba(243,238,231,0.45)',
  transition: 'border-color 120ms ease, background 120ms ease',
  '&:hover:not(:disabled)': { borderColor: BRAND.parchment, background: 'rgba(243,238,231,0.06)' },
  '&:disabled': { opacity: 0.6, cursor: 'default' },
});

const Status = styled('p')({
  margin: '2px 0 0',
  fontFamily: FONTS.body,
  fontSize: 13.5,
  textAlign: 'center',
  color: 'rgba(243,238,231,0.75)',
});

/**
 * Continue with Google, or with LinkedIn.
 *
 * "Continue" rather than "Sign up with" or "Log in with", because for these
 * the two are the same button: the provider knows whether the account exists,
 * and asking the person to know first is asking them to remember whether they
 * have been here before. LinkedIn earns its place for this audience in
 * particular — Climate Week is a professional week, and most people in it
 * already have one open.
 */
export function SocialButtons() {
  const [busy, setBusy] = useState<OAuthProvider | null>(null);
  const [message, setMessage] = useState('');

  const go = async (provider: OAuthProvider) => {
    setBusy(provider);
    setMessage('');
    const result = await authClient.startOAuth(provider);
    setBusy(null);
    if (!result.ok) setMessage(result.message);
  };

  return (
    <SocialStack>
      <SocialButton type="button" onClick={() => go('google')} disabled={busy !== null}>
        <GoogleMark />
        {busy === 'google' ? `Connecting to ${PROVIDER_NAME.google}…` : 'Continue with Google'}
      </SocialButton>
      <SocialButton type="button" onClick={() => go('linkedin')} disabled={busy !== null}>
        <LinkedInMark />
        {busy === 'linkedin' ? `Connecting to ${PROVIDER_NAME.linkedin}…` : 'Continue with LinkedIn'}
      </SocialButton>
      {message && <Status role="status">{message}</Status>}
    </SocialStack>
  );
}

function GoogleMark() {
  return (
    <svg width={20} height={20} viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path
        fill="#FFC107"
        d="M43.61 20.08H42V20H24v8h11.3C33.65 32.66 29.22 36 24 36c-6.63 0-12-5.37-12-12s5.37-12 12-12c3.06 0 5.84 1.15 7.96 3.04l5.66-5.66C34.05 6.05 29.27 4 24 4 12.95 4 4 12.95 4 24s8.95 20 20 20 20-8.95 20-20c0-1.34-.14-2.65-.39-3.92z"
      />
      <path
        fill="#FF3D00"
        d="m6.31 14.69 6.57 4.82C14.66 15.11 18.96 12 24 12c3.06 0 5.84 1.15 7.96 3.04l5.66-5.66C34.05 6.05 29.27 4 24 4 16.32 4 9.66 8.34 6.31 14.69z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.17 0 9.86-1.98 13.41-5.19l-6.19-5.24A11.9 11.9 0 0 1 24 36c-5.2 0-9.62-3.32-11.28-7.95l-6.52 5.03C9.5 39.56 16.23 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.61 20.08H42V20H24v8h11.3a12.04 12.04 0 0 1-4.09 5.57l6.19 5.24C36.97 39.21 44 34 44 24c0-1.34-.14-2.65-.39-3.92z"
      />
    </svg>
  );
}

function LinkedInMark() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect width="24" height="24" rx="4" fill="#0A66C2" />
      <path
        fill="#fff"
        d="M7.2 9.5H4.7v9.8h2.5V9.5zM5.95 5.2a1.45 1.45 0 1 0 0 2.9 1.45 1.45 0 0 0 0-2.9zM19.3 13.7c0-2.64-1.4-4.4-3.83-4.4-1.2 0-2.05.66-2.42 1.3V9.5h-2.4v9.8h2.5v-4.86c0-1.28.25-2.52 1.83-2.52 1.56 0 1.58 1.46 1.58 2.6v4.78h2.5l.24-5.6z"
      />
    </svg>
  );
}

/*
 * The styled pieces are exported through plain function components rather than
 * directly. Fast Refresh only preserves state across edits in a file whose
 * exports are all components, and it cannot tell that the result of `styled()`
 * is one.
 */

export function FormStack(props: ComponentProps<typeof FormStackRoot>) {
  return <FormStackRoot {...props} />;
}

/** A route that should look like the page's main action. */
export function PrimaryLink(props: ComponentProps<typeof PrimaryLinkRoot>) {
  return <PrimaryLinkRoot {...props} />;
}

export function Note(props: ComponentProps<typeof NoteRoot>) {
  return <NoteRoot {...props} />;
}

export function RouterLink(props: ComponentProps<typeof RouterLinkRoot>) {
  return <RouterLinkRoot {...props} />;
}

export function Footer(props: ComponentProps<typeof FooterRoot>) {
  return <FooterRoot {...props} />;
}
